type APN = {
  address: string;
  user: string;
  password: string;
};

type IP = {
  ip: string;
  port: number;
};

type DNS = {
  address: string;
  port: number;
};

type Password = {
  old: string;
  new: string;
};

type DataTransmission = number;

type LockTypeProgression = {
  n1: number;
  n2: number;
};

type IgnitionByVoltage = {
  initial: number;
  final: number;
};

export class BWS4GLoraEncoder {
  //comands

  static imei(): string {
    return `IMEI\r\n`;
  }

  static iccid(): string {
    return `ICCID\r\n`;
  }

  static firmware(): string {
    return `ET\r\n`;
  }

  static apn(props: APN): string | undefined {
    const address = props?.address ? props.address : ' ';
    const user = props?.user ? props.user : ' ';
    const password = props?.password ? props.password : ' ';
    return `APN*${address}*${user}*${password}\r\n`;
  }

  static ip_primary(props: IP): string | undefined {
    if (!props?.ip || !props?.port) return undefined;
    return `IP1#${props.ip}#${props.port}#\r\n`;
  }

  static ip_secondary(props: IP): string | undefined {
    if (!props?.ip || !props?.port) return undefined;
    return `IP2#${props.ip}#${props.port}#\r\n`;
  }

  static dns_primary(props: DNS): string | undefined {
    const address = props?.address ? props.address : ' ';
    const port = props?.port ? props.port : ' ';
    return `DNS1#${address}#${port}#\r\n`;
  }

  static dns_secondary(props: DNS): string | undefined {
    const address = props?.address ? props.address : ' ';
    const port = props?.port ? props.port : ' ';
    return `DNS2#${address}#${port}#\r\n`;
  }

  static password(props: Password): string | undefined {
    if (!props?.old || !props?.new) {
      return undefined;
    }
    return `MODIFYPW${props.old}${props.new}\r\n`;
  }

  static lock_type(props: number): string | undefined {
    if (typeof props !== 'number' || [30, 31].every((v) => v !== props)) {
      return undefined;
    }
    return `MODE${props}\r\n`;
  }

  static lock_type_variation(props: number): string | undefined {
    if (typeof props !== 'number' || [1, 2].every((v) => v !== props)) {
      return undefined;
    }
    return `MODE${props}\r\n`;
  }

  static data_transmission_on(props: DataTransmission): string | undefined {
    if (typeof props !== 'number' || Number.isNaN(props)) return undefined;
    return `HB${props}\r\n`;
  }

  static data_transmission_off(props: DataTransmission): string | undefined {
    if (typeof props !== 'number' || Number.isNaN(props)) return undefined;
    return `SHB${props}\r\n`;
  }

  // Firmware do BWS 4G multiplica por 10 ao retornar no CHECK.
  // Para o usuário ver "10" precisamos enviar DK1; para ver "50000" enviamos DK5000.
  static odometer(props: number | string): string | undefined {
    const value = typeof props === 'string' ? Number(props) : props;
    if (Number.isNaN(value)) {
      return undefined;
    }
    return `DK${Math.trunc(value / 10)}\r\n`;
  }

  static keep_alive(props: number): string | undefined {
    if (typeof props !== 'number' || Number.isNaN(props) || props === 0) {
      return undefined;
    }
    return `TX${props}\r\n`;
  }

  static accelerometer_sensitivity(props: number): string | undefined {
    if (typeof props !== 'number' || Number.isNaN(props) || props < 0 || props > 10) {
      return undefined;
    }
    return `SENSE${props}\r\n`;
  }

  static sensitivity_adjustment(props: number): string | undefined {
    if (typeof props !== 'number' || Number.isNaN(props) || props < 0 || props > 2000) {
      return undefined;
    }
    return `GS${props}\r\n`;
  }

  static lbs_position(props: boolean): string | undefined {
    if (typeof props !== 'boolean') {
      return undefined;
    }
    return `LBS${props ? 'ON' : 'OFF'}\r\n`;
  }

  static led(props: boolean): string | undefined {
    if (typeof props !== 'boolean') {
      return undefined;
    }
    return `LED${props ? 'ON' : 'OFF'}\r\n`;
  }

  static communication_type(props: string): string | undefined {
    if (!['TCP'].includes(props)) {
      return undefined;
    }
    return `${props}\r\n`;
  }

  static anti_theft(props: boolean): string | undefined {
    if (typeof props !== 'boolean') {
      return undefined;
    }
    return `AF${props ? 'ON' : 'OFF'}\r\n`;
  }

  static sms(props: boolean): string | undefined {
    if (typeof props !== 'boolean') {
      return undefined;
    }
    return `SMS${props ? '1' : '0'}\r\n`;
  }

  static economy_mode(props: number): string | undefined {
    if (typeof props !== 'number' || ![0, 1, 2].includes(props)) {
      return undefined;
    }
    return `SDMS${props}\r\n`;
  }

  static network_mode(props: 'AUTO' | '4G' | '2G'): string | undefined {
    if (props === 'AUTO') return BWS4GLoraEncoder.network_mode_auto();
    if (props === '4G') return BWS4GLoraEncoder.network_mode_4g();
    if (props === '2G') return BWS4GLoraEncoder.network_mode_2g();
    return undefined;
  }

  static network_mode_auto(): string {
    return `SETNETWORKMODEAUTO\r\n`;
  }

  static network_mode_4g(): string {
    return `SETNETWORKMODE4G\r\n`;
  }

  static network_mode_2g(): string {
    return `SETNETWORKMODE2G\r\n`;
  }

  static get_network_mode(): string {
    return `GETNETWORKMODE\r\n`;
  }

  static gps_filter_speed(props: boolean): string | undefined {
    if (typeof props !== 'boolean') {
      return undefined;
    }
    return `setgpsfilterspeed${props ? '1' : '0'}\r\n`;
  }

  static get_gps_filter_speed(): string {
    return `getgpsfilterspeed\r\n`;
  }

  static gps_periodic_wakeup(props: boolean): string | undefined {
    if (typeof props !== 'boolean') {
      return undefined;
    }
    return `setgpsperiodicwakeup${props ? '1' : '0'}\r\n`;
  }

  static get_gps_periodic_wakeup(): string {
    return `getgpsperiodicwakeup\r\n`;
  }

  static ignition_type(props: 0 | 1 | 2): string | undefined {
    if (![0, 1, 2].includes(props)) {
      return undefined;
    }
    return `setignitiontype${props}\r\n`;
  }

  static get_ignition_type(): string {
    return `getignitiontype\r\n`;
  }

  static enable_recommended(): string {
    return `RECOM00000#\r\n`;
  }

  static registration(): string {
    return `REG000000#\r\n`;
  }

  static set_ack(props: 0 | 1): string {
    return `SETACK${props}\r\n`;
  }

  static get_ack(): string {
    return `GETACKG\r\n`;
  }

  static config_start(): string {
    return `CONFIG_START\r\n`;
  }

  static config_stop(): string {
    return `CONFIG_STOP\r\n`;
  }

  static set_always_get_agps(props: boolean): string | undefined {
    if (typeof props !== 'boolean') {
      return undefined;
    }
    return `setalwaysgetagps${props ? '1' : '0'}\r\n`;
  }

  static get_always_get_agps(): string {
    return `getalwaysgetagps\r\n`;
  }

  static check(): string {
    return `CHECK\r\n`;
  }

  static cxip(): string {
    return `CXIP\r\n`;
  }

  static status(): string {
    return `STATUS\r\n`;
  }

  static autotest(): string {
    return `AUTOTEST\r\n`;
  }

  static lora_version(): string {
    return `LORAVERSION\r\n`;
  }

  static ble_version(): string {
    return `BLEVERSION\r\n`;
  }

  static rs232_mode(mode: number): string {
    return `setrs232mode${mode}\r\n`;
  }

  static rs232_send(text: string): string {
    return `rs232send;${text}\r\n`;
  }

  static start(): string {
    return `START\r\n`;
  }

  static restart(): string {
    return `RESTART\r\n`;
  }

  static debug_on(): string {
    return `DN\r\n`;
  }

  static debug_off(): string {
    return `DF\r\n`;
  }

  static batch_query(commands?: string[]): string {
    const defaultCommands = [
      'IMEI',
      'CHECK',
      'CXIP',
      'ET',
      'GOOGLE',
      'getignitiontype',
      'getgpsperiodicwakeup',
      'getalwaysgetagps',
      'getgpsfilterspeed',
      'GETNETWORKMODE',
      'GETACKG',
    ];
    const cmds = commands ?? defaultCommands;
    return `*9;${cmds.join(';')}\r\n`;
  }

  static horimeter(props: number): string | undefined {
    if (typeof props !== 'number' || Number.isNaN(props) || props === 0) {
      return undefined;
    }
    return `HR${props}\r\n`;
  }

  static jammer_detection(props: boolean): string | undefined {
    if (typeof props !== 'boolean') {
      return undefined;
    }
    return `JD${props ? 1 : 0}\r\n`;
  }

  static clear_buffer(props: boolean): string | undefined {
    if (typeof props !== 'boolean' || props === false) {
      return undefined;
    }
    return 'CLEARBUFFER\r\n';
  }

  static clear_horimeter(props: boolean): string | undefined {
    if (typeof props !== 'boolean' || props === false) {
      return undefined;
    }
    return 'CLEARHORIMETER\r\n';
  }

  static input_1(props: number): string | undefined {
    if (props !== 1 && props !== 2) {
      return undefined;
    }
    if (props === 1) {
      return 'MASTER1\r\n';
    }
    if (props === 2) {
      return 'SLAVE1\r\n';
    }
    return undefined;
  }

  static input_2(props: number): string | undefined {
    if (props !== 1 && props !== 2 && props !== 3) {
      return undefined;
    }
    if (props === 1) {
      return 'MASTER2\r\n';
    }
    if (props === 2) {
      return 'SLAVE2\r\n';
    }
    if (props === 3) {
      return 'NEGATIVE2\r\n';
    }
    return undefined;
  }

  static angle_adjustment(props: number): string | undefined {
    if (typeof props !== 'number' || Number.isNaN(props)) {
      return undefined;
    }
    return `TDET${props}\r\n`;
  }

  static lock_type_progression(props: LockTypeProgression): string | undefined {
    if (!props?.n1 || !props?.n2) {
      return undefined;
    }
    return `DC*${props.n1}*${props.n2}\r\n`;
  }

  static ignition_by_voltage(props: IgnitionByVoltage): string | undefined {
    if (!props?.initial || !props?.final) {
      return undefined;
    }
    return `VOLTAGE*${props.initial}*${props.final}\r\n`;
  }

  static max_speed(props: number): string | undefined {
    if (typeof props !== 'number' || Number.isNaN(props)) {
      return undefined;
    }
    return `SPEED${props}\r\n`;
  }

  static ignition_status_hb(props: boolean): string | undefined {
    if (typeof props !== 'boolean') {
      return undefined;
    }
    return `ISHB${props ? 'ON' : 'OFF'}\r\n`;
  }

  static gps_replay_fix_sleep(props: boolean): string | undefined {
    if (typeof props !== 'boolean') {
      return undefined;
    }
    return `setgpsreplayfixsleep${props ? '1' : '0'}\r\n`;
  }

  static improper_movement(props: boolean): string | undefined {
    if (typeof props !== 'boolean') {
      return undefined;
    }
    return `setimpropermovement${props ? '1' : '0'}\r\n`;
  }

  static get_improper_movement(): string {
    return `getimpropermovement\r\n`;
  }

  static methods() {
    return {
      apn: BWS4GLoraEncoder.apn,
      ip_primary: BWS4GLoraEncoder.ip_primary,
      ip_secondary: BWS4GLoraEncoder.ip_secondary,
      dns_primary: BWS4GLoraEncoder.dns_primary,
      dns_secondary: BWS4GLoraEncoder.dns_secondary,
      password: BWS4GLoraEncoder.password,
      lock_type: BWS4GLoraEncoder.lock_type,
      lock_type_variation: BWS4GLoraEncoder.lock_type_variation,
      data_transmission_on: BWS4GLoraEncoder.data_transmission_on,
      data_transmission_off: BWS4GLoraEncoder.data_transmission_off,
      odometer: BWS4GLoraEncoder.odometer,
      keep_alive: BWS4GLoraEncoder.keep_alive,
      accelerometer_sensitivity: BWS4GLoraEncoder.accelerometer_sensitivity,
      sensitivity_adjustment: BWS4GLoraEncoder.sensitivity_adjustment,
      lbs_position: BWS4GLoraEncoder.lbs_position,
      led: BWS4GLoraEncoder.led,
      communication_type: BWS4GLoraEncoder.communication_type,
      anti_theft: BWS4GLoraEncoder.anti_theft,
      sms: BWS4GLoraEncoder.sms,
      economy_mode: BWS4GLoraEncoder.economy_mode,
      network_mode: BWS4GLoraEncoder.network_mode,
      network_mode_auto: BWS4GLoraEncoder.network_mode_auto,
      network_mode_4g: BWS4GLoraEncoder.network_mode_4g,
      network_mode_2g: BWS4GLoraEncoder.network_mode_2g,
      get_network_mode: BWS4GLoraEncoder.get_network_mode,
      gps_filter_speed: BWS4GLoraEncoder.gps_filter_speed,
      get_gps_filter_speed: BWS4GLoraEncoder.get_gps_filter_speed,
      gps_periodic_wakeup: BWS4GLoraEncoder.gps_periodic_wakeup,
      get_gps_periodic_wakeup: BWS4GLoraEncoder.get_gps_periodic_wakeup,
      ignition_type: BWS4GLoraEncoder.ignition_type,
      get_ignition_type: BWS4GLoraEncoder.get_ignition_type,
      enable_recommended: BWS4GLoraEncoder.enable_recommended,
      registration: BWS4GLoraEncoder.registration,
      set_ack: BWS4GLoraEncoder.set_ack,
      get_ack: BWS4GLoraEncoder.get_ack,
      config_start: BWS4GLoraEncoder.config_start,
      config_stop: BWS4GLoraEncoder.config_stop,
      always_get_agps: BWS4GLoraEncoder.set_always_get_agps,
      get_always_get_agps: BWS4GLoraEncoder.get_always_get_agps,
      batch_query: BWS4GLoraEncoder.batch_query,
      check: BWS4GLoraEncoder.check,
      cxip: BWS4GLoraEncoder.cxip,
      status: BWS4GLoraEncoder.status,
      autotest: BWS4GLoraEncoder.autotest,
      start: BWS4GLoraEncoder.start,
      restart: BWS4GLoraEncoder.restart,
      debug_on: BWS4GLoraEncoder.debug_on,
      debug_off: BWS4GLoraEncoder.debug_off,
      horimeter: BWS4GLoraEncoder.horimeter,
      jammer_detection: BWS4GLoraEncoder.jammer_detection,
      clear_buffer: BWS4GLoraEncoder.clear_buffer,
      clear_horimeter: BWS4GLoraEncoder.clear_horimeter,
      input_1: BWS4GLoraEncoder.input_1,
      input_2: BWS4GLoraEncoder.input_2,
      angle_adjustment: BWS4GLoraEncoder.angle_adjustment,
      lock_type_progression: BWS4GLoraEncoder.lock_type_progression,
      ignition_by_voltage: BWS4GLoraEncoder.ignition_by_voltage,
      max_speed: BWS4GLoraEncoder.max_speed,
      ignition_status_hb: BWS4GLoraEncoder.ignition_status_hb,
      gps_replay_fix_sleep: BWS4GLoraEncoder.gps_replay_fix_sleep,
      improper_movement: BWS4GLoraEncoder.improper_movement,
      get_improper_movement: BWS4GLoraEncoder.get_improper_movement,
      imei: BWS4GLoraEncoder.imei,
      iccid: BWS4GLoraEncoder.iccid,
      firmware: BWS4GLoraEncoder.firmware,
    };
  }

  static encoder<
    TMethods extends Record<string, (...args: any[]) => any>,
    K extends keyof TMethods,
  >(
    this: { methods(): TMethods },
    props: { command: K; args: Parameters<TMethods[K]>[0] },
  ): ReturnType<TMethods[K]> {
    const { command, args } = props;
    const methods = BWS4GLoraEncoder.methods();
    const methodFunction = (methods as Record<string, unknown>)[command as string] as TMethods[K];

    return methodFunction?.(args);
  }
}
